import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "rest-api",
  name: "REST API設計",
  nameEn: "REST API",
  seo: {
    title: "REST API設計を学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "REST APIはWebサービスの通信を支える重要な技術です。ゆめさくエンジニアならHTTPメソッド・ステータスコード・認証の設計を実践的に学べます。",
    url: "/columns/rest-api-programming-school",
  },
  hero: {
    title: "REST API設計を学べる女性向けプログラミングスクール",
    englishTitle: "Learn REST API",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "REST API設計" },
  ],
  overview: {
    heading: "REST APIとは？",
    paragraphs: [
      "REST（Representational State Transfer）APIとは、Web上でサービス同士がデータをやり取りするための設計原則です。Webブラウザとサーバー間だけでなく、モバイルアプリやマイクロサービス間の通信でも広く使われています。",
      "HTTPメソッド（GET/POST/PUT/DELETE）とURLの組み合わせでリソースを操作するシンプルな設計が特徴で、フロントエンドとバックエンドを分離するモダンなWeb開発には欠かせない技術です。",
      "Twitter、Stripe、GitHub、LINEなど、ほぼすべてのWebサービスがREST APIを公開しており、サービス間連携や外部データ活用の基盤となっています。",
    ],
    useCases: [
      {
        label: "フロントエンドとバックエンドの連携",
        description:
          "ReactやVue.jsで作ったフロントエンドと、サーバーサイドのビジネスロジックをJSON形式のAPIで接続します。",
      },
      {
        label: "モバイルアプリのバックエンド",
        description:
          "iOS・Androidアプリからサーバーへのデータ送受信はREST APIが基本です。ユーザー認証やデータ同期に不可欠です。",
      },
      {
        label: "外部サービス連携",
        description:
          "決済（Stripe）、メール送信（SendGrid）、地図（Google Maps）など外部APIを組み合わせてサービスを構築します。",
      },
      {
        label: "マイクロサービス間通信",
        description:
          "大規模システムを機能ごとに分割したマイクロサービス同士がREST APIで連携し、柔軟にスケールできるアーキテクチャを実現します。",
      },
    ],
  },
  whyLearn: {
    heading: "REST API設計を学ぶべき3つの理由",
    items: [
      {
        title: "モダンなWeb開発の必須スキル",
        description:
          "SPA（シングルページアプリケーション）が主流となった現在、フロントエンドとバックエンドの通信はREST APIが標準です。どのフレームワークを使う場合でも、API設計の理解は必須のスキルとなっています。",
      },
      {
        title: "フルスタックエンジニアへの道",
        description:
          "REST API設計を理解することで、フロントエンドとバックエンドの両方を一人で開発できるフルスタックエンジニアへのキャリアが開けます。チーム内での役割の幅も広がります。",
      },
      {
        title: "設計力はどの言語でも通用する",
        description:
          "REST APIの設計原則はプログラミング言語に依存しません。一度身につけた設計力は、Java、Python、Node.jsなど、どの技術スタックでも活かすことができます。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでREST API設計を学ぶ",
    paragraphs: [
      "ゆめさくエンジニアではPhase 2の応用としてREST API設計を体系的に学びます。単にAPIを「使う」だけでなく、「設計する」力を養うことで、チーム開発で頼られるエンジニアを目指します。",
      "実際にAPIを設計・実装し、フロントエンドと接続するプロジェクトを通じて、現場で通用する実践力を身につけます。",
    ],
    topics: [
      "HTTPプロトコルの基礎（メソッド・ステータスコード・ヘッダー）",
      "RESTful設計原則とエンドポイント命名規則",
      "リクエスト・レスポンスのJSON設計",
      "認証・認可（JWT・OAuth2.0）",
      "エラーハンドリングとバリデーション",
      "API仕様書作成（OpenAPI/Swagger）",
    ],
  },
  careerPaths: {
    heading: "REST API設計を活かせるキャリア",
    items: [
      {
        title: "バックエンドエンジニア",
        description:
          "WebアプリケーションのサーバーサイドロジックとAPIの設計・開発を担当します。データベース設計、パフォーマンス最適化、セキュリティ対策など幅広いスキルが求められます。",
        salary: "500〜800万円",
      },
      {
        title: "フルスタックエンジニア",
        description:
          "フロントエンドからバックエンド、API設計、インフラまで一貫して対応できるエンジニアです。スタートアップでは特に重宝され、高い裁量を持って働けます。",
        salary: "550〜900万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "3〜6ヶ月（断片的な知識の寄せ集め）",
        luagate: "2〜3ヶ月（体系的なカリキュラム）",
      },
      {
        label: "挫折率",
        selfStudy: "設計原則が抽象的で理解に苦しむ",
        luagate: "実際のAPI開発を通じて設計原則を体感",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "チュートリアルのコピペで終わりがち",
        luagate: "自分でAPI仕様を設計し、フロントと接続するプロジェクト",
      },
      {
        label: "質問対応",
        selfStudy: "設計の良し悪しを判断できない",
        luagate: "現役エンジニアがコードレビューで設計を指導",
      },
      {
        label: "就職サポート",
        selfStudy: "すべて自力で進める",
        luagate: "ポートフォリオ添削・面接対策まで一貫サポート",
      },
    ],
  },
  faq: [
    {
      question: "REST APIとGraphQLの違いは何ですか？",
      answer:
        "REST APIはリソースごとにエンドポイントを分けるシンプルな設計で、広く普及しています。GraphQLはクライアントが必要なデータだけを柔軟に取得できる仕組みです。まずはREST APIの設計原則を理解し、その後GraphQLに進むのが効率的です。",
    },
    {
      question: "API設計を学ぶのに特定のプログラミング言語の知識は必要ですか？",
      answer:
        "REST API設計の原則自体は言語非依存ですが、実際に手を動かすためには何らかのサーバーサイド言語の基礎が必要です。ゆめさくエンジニアではPhase 1〜2で基礎を学んだ後に進むため、前提知識は不要です。",
    },
    {
      question: "APIのセキュリティはどのように学べますか？",
      answer:
        "ゆめさくエンジニアのカリキュラムではJWTトークンによる認証、OAuth2.0、CORS設定、レート制限など、実務で必要なセキュリティ対策を実践的に学びます。脆弱なAPIの具体例を見ながら対策を理解していきます。",
    },
    {
      question: "REST API設計だけでエンジニアとして就職できますか？",
      answer:
        "API設計単体というよりは、バックエンド開発スキルの一部として評価されます。ゆめさくエンジニアではプログラミング言語・データベース・インフラの知識とセットで学ぶため、実務で通用する総合的なスキルが身につきます。",
    },
  ],
  relatedLanguages: [
    { slug: "java", name: "Java" },
    { slug: "javascript", name: "JavaScript" },
    { slug: "system-design", name: "システム設計" },
    { slug: "docker", name: "Docker / CI-CD" },
  ],
};
